import { useState, useRef, useCallback, useEffect } from 'react';
import { Sentry } from '../../../../services/sentryService';

export interface AudioClip {
  id: string;
  blob: Blob;
  url: string;
  duration: number;
  /** mimeType actually used by MediaRecorder for this clip. Stored at
      capture time so cross-device draft restores don't pick up a stale
      type from `blob.type` when merging clips. */
  mimeType: string;
}

/** Error categories surfaced from getUserMedia rejection. Maps to the
    DOMException `name` so the UI can show specific copy. */
export type AudioRecorderErrorName =
  | 'NotAllowedError'  // user denied permission
  | 'NotFoundError'    // no microphone present
  | 'NotReadableError' // mic exists but already in use
  | 'GenericError';

interface AudioRecorderError {
  name: AudioRecorderErrorName;
  message: string;
}

interface AudioRecorderState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  clips: AudioClip[];
  audioLevel: number;
  /** Per-bin FFT magnitudes (0..255), exponentially smoothed per frame.
      Length = analyser.frequencyBinCount = fftSize/2 = 128. Null until
      recording starts. */
  frequencyData: Uint8Array | null;
  error: AudioRecorderError | null;
  /** True if the last pause came from a `visibilitychange` event (app
      backgrounded) rather than a user tap. Surface shows a note so the
      user knows they need to resume manually. */
  wasAutoPaused: boolean;
}

function describeMicError(err: unknown): AudioRecorderError {
  const name = (err && typeof err === 'object' && 'name' in err && typeof (err as { name?: unknown }).name === 'string')
    ? (err as { name: string }).name
    : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return { name: 'NotAllowedError', message: 'Microphone access denied. Grant permission in your browser to record.' };
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return { name: 'NotFoundError', message: 'No microphone found on this device.' };
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return { name: 'NotReadableError', message: 'Microphone is in use by another app. Close it and try again.' };
  }
  return { name: 'GenericError', message: "Couldn't access the microphone. Please try again." };
}

/** Pick a mimeType the browser actually supports */
function getSupportedMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg'];
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return ''; // let browser pick default
}

export const useAudioRecorder = () => {
  const [state, setState] = useState<AudioRecorderState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    clips: [],
    audioLevel: 0,
    frequencyData: null,
    error: null,
    wasAutoPaused: false,
  });

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  /** When set true, the next `onstop` will throw away the recorded chunks
      instead of producing a clip. Used to honour stopRecording({ discard }). */
  const discardNextRef = useRef<boolean>(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAnalyser = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setState(prev => ({ ...prev, audioLevel: 0 }));
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      mediaRecorder.current = mimeType
        ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 96_000 })
        : new MediaRecorder(stream, { audioBitsPerSecond: 96_000 });
      chunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      /* Why a recording ended. A clip is only ever produced by stopRecording(),
         so a session that splits into several clips without the participant
         tapping Stop means something else killed the recorder — a hardware
         error, or the OS revoking the mic track when the app is backgrounded.
         Neither surfaces anywhere today, and the phones this happens on are
         never in our hands. Report both. */
      const startedAt = Date.now();
      const report = (event: string) => {
        const detail = {
          event,
          mimeType: mediaRecorder.current?.mimeType || 'unknown',
          elapsedSec: Math.round((Date.now() - startedAt) / 1000),
          state: mediaRecorder.current?.state || 'gone',
        };
        console.warn('[useAudioRecorder]', detail);
        Sentry.captureMessage(`audio recorder: ${event}`, { level: 'warning', extra: detail });
      };
      mediaRecorder.current.onerror = () => report('mediarecorder-error');
      // Fires when the OS hands the mic to another app or the track dies.
      stream.getAudioTracks().forEach(t => { t.onended = () => report('track-ended'); });

      mediaRecorder.current.onstop = () => {
        // If the most recent stop was a discard (too-short clip), drop the
        // chunks on the floor without producing a clip. Reset the flag so
        // the next stop behaves normally.
        if (discardNextRef.current) {
          discardNextRef.current = false;
          chunks.current = [];
          return;
        }
        const recordedType = mediaRecorder.current?.mimeType || getSupportedMimeType() || 'audio/webm';
        const blob = new Blob(chunks.current, { type: recordedType });
        const url = URL.createObjectURL(blob);
        setState(prev => ({
          ...prev,
          clips: [...prev.clips, {
            id: `clip-${Date.now()}`,
            blob,
            url,
            duration: prev.duration,
            mimeType: recordedType,
          }],
        }));
      };

      mediaRecorder.current.start();

      /* Audio analyser for live waveform */
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      // Per-bin exponentially-smoothed buffer so the visualization doesn't jitter.
      // smoothed = α * raw + (1-α) * smoothed; α=0.3 gives a calm but responsive feel.
      const smoothed = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          for (let i = 0; i < dataArray.length; i++) {
            // Math.round to keep values within Uint8 without precision drift.
            smoothed[i] = Math.round(0.3 * dataArray[i] + 0.7 * smoothed[i]);
          }
          const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
          // Pass a fresh copy each frame so React detects the change.
          setState(prev => ({ ...prev, audioLevel: avg / 255, frequencyData: new Uint8Array(smoothed) }));
          animFrameRef.current = requestAnimationFrame(updateLevel);
        }
      };
      updateLevel();

      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        duration: 0,
        error: null,
        wasAutoPaused: false,
      }));

      timerRef.current = setInterval(() => {
        setState(prev => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);
    } catch (err) {
      setState(prev => ({ ...prev, error: describeMicError(err) }));
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current.pause();
      clearTimer();
      setState(prev => ({ ...prev, isPaused: true }));
    }
  }, [clearTimer]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorder.current?.state === 'paused') {
      mediaRecorder.current.resume();
      timerRef.current = setInterval(() => {
        setState(prev => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);
      // Clear the auto-pause flag — user has explicitly resumed.
      setState(prev => ({ ...prev, isPaused: false, wasAutoPaused: false }));
    }
  }, []);

  const stopRecording = useCallback((options?: { discard?: boolean }) => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      // Signal the onstop handler whether to keep or drop the recorded chunks.
      // Set BEFORE calling .stop() so the handler sees the flag.
      discardNextRef.current = !!options?.discard;
      mediaRecorder.current.stop();
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      clearTimer();
      stopAnalyser();
      setState(prev => ({ ...prev, isRecording: false, isPaused: false, wasAutoPaused: false }));
    }
  }, [clearTimer, stopAnalyser]);

  /** Clear an in-progress auto-pause without resuming the recorder. */
  const clearAutoPaused = useCallback(() => {
    setState(prev => prev.wasAutoPaused ? { ...prev, wasAutoPaused: false } : prev);
  }, []);

  /** Clear any error so the surface can re-render its normal UI. */
  const clearError = useCallback(() => {
    setState(prev => prev.error ? { ...prev, error: null } : prev);
  }, []);

  const deleteClip = useCallback((clipId: string) => {
    setState(prev => {
      const clip = prev.clips.find(c => c.id === clipId);
      if (clip) URL.revokeObjectURL(clip.url);
      return { ...prev, clips: prev.clips.filter(c => c.id !== clipId) };
    });
  }, []);

  /** Inject pre-existing clips (e.g. from a restored draft) */
  const injectClips = useCallback((clips: AudioClip[]) => {
    setState(prev => ({ ...prev, clips }));
  }, []);

  const resetAll = useCallback(() => {
    setState(prev => {
      prev.clips.forEach(c => URL.revokeObjectURL(c.url));
      return {
        isRecording: false,
        isPaused: false,
        duration: 0,
        clips: [],
        audioLevel: 0,
        frequencyData: null,
        error: null,
        wasAutoPaused: false,
      };
    });
  }, []);

  /* Cleanup on unmount — also revoke every clip URL we created. The previous
     implementation only revoked URLs on explicit delete / resetAll, leaking
     them when the parent unmounted mid-flow (e.g. user closes the task
     without submitting). */
  useEffect(() => {
    return () => {
      clearTimer();
      stopAnalyser();
      if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
        mediaRecorder.current.stop();
        mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      }
      // Drain object URLs. setState callback form because plain `state.clips`
      // captured here may be stale.
      setState(prev => {
        prev.clips.forEach(c => URL.revokeObjectURL(c.url));
        return prev;
      });
    };
  }, [clearTimer, stopAnalyser]);

  /* App-switch / visibility — when the page is hidden while recording, pause
     automatically and flag the pause as auto. iOS in particular will start
     buffering oddly if the tab keeps recording in the background. We never
     auto-resume; the user has to tap play to come back. */
  useEffect(() => {
    const handler = () => {
      if (document.hidden && mediaRecorder.current?.state === 'recording') {
        mediaRecorder.current.pause();
        clearTimer();
        setState(prev => ({ ...prev, isPaused: true, wasAutoPaused: true }));
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [clearTimer]);

  return {
    ...state,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    deleteClip,
    injectClips,
    resetAll,
    clearAutoPaused,
    clearError,
  };
};
